import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Clock, ArrowRight } from "lucide-react";
import Seo from "@/components/common/Seo";
import Reveal from "@/components/common/Reveal";
import { GUIDES } from "@/data/guides";

export default function Guides() {
  return (
    <div className="bg-[var(--acx-bg)]">
      <Seo
        title="Car Care Guides & Detailing Tips"
        description="Expert AutoCareX detailing guides: how to remove swirl marks, the best car shampoo for black cars, long-lasting tyre shine, interior detailing and AC duct cleaning."
      />

      <section className="relative acx-noise overflow-hidden border-b border-[color:var(--acx-border)]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(800px_circle_at_50%_0%,rgba(225,27,34,0.10),transparent_60%)]" />
        <div className="acx-container relative px-4 py-16 text-center sm:px-6 lg:px-10 lg:py-24">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-white/50">
              <BookOpen size={14} /> Car Care Hub
            </span>
            <h1 className="mt-3 font-display text-[40px] font-extrabold leading-[1.04] tracking-[-0.03em] sm:text-6xl">Detailing Guides &amp; Tips</h1>
            <p className="mx-auto mt-5 max-w-xl text-[17px] leading-relaxed text-white/65">
              Practical, no-nonsense advice from the AutoCareX team — get professional results at home.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="acx-container px-4 py-14 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="guides-grid">
          {GUIDES.map((g, i) => (
            <motion.div
              key={g.slug}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <Link
                to={`/guides/${g.slug}`}
                data-testid={`guide-card-${g.slug}`}
                className="group flex h-full flex-col overflow-hidden rounded-[18px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] transition-colors hover:border-white/20"
              >
                <div className="aspect-[16/10] overflow-hidden bg-[var(--acx-bg-2)]">
                  <img src={g.cover} alt={g.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center gap-3 text-[12px] text-white/50">
                    <span className="rounded-full bg-[#E11B22]/15 px-2.5 py-1 font-semibold text-[#E11B22]">{g.category}</span>
                    <span className="inline-flex items-center gap-1"><Clock size={12} /> {g.readTime}</span>
                  </div>
                  <h2 className="mt-3 font-display text-[19px] font-bold leading-snug tracking-[-0.01em]">{g.title}</h2>
                  <p className="mt-2 flex-1 text-[14px] leading-relaxed text-white/60">{g.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-white">
                    Read guide <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
